import { useEffect } from "react";
import { usePersistStore } from "../store/persistStore";
import { useGetSurveySessionByUserIdQuery } from "./react-query/useSurveySessionByUserId";
import { useCurrentUser } from "./react-query/useCurrentUser";
import { getMedicalProvider } from "../utils/surveyUtils";
import { useQuery } from "@tanstack/react-query";
import { AppEntrypointService } from "../services/appEntrypointService";
import type { MSO } from "../typings/enums";
import type { SurveySession } from "../typings";

export const useGetMsoData = () => {
  const { mso, setMso, selectedMembership } = usePersistStore();

  const { data: surveySession } = useGetSurveySessionByUserIdQuery();
  const { data: userProfile } = useCurrentUser();

  const determinedSelectedMembership =
    selectedMembership || userProfile?.selectedMembership;

  // Try to get MSO from product-route API if we have a selected product
  const {
    data: apiProductData,
    isLoading: isLoadingApiProduct,
    isFetching: isFetchingApiProduct,
  } = useQuery({
    queryKey: ["product-route-mso", determinedSelectedMembership],
    queryFn: async () => {
      if (!determinedSelectedMembership) throw new Error("No product selected");
      return AppEntrypointService.getProductRoute(
        determinedSelectedMembership as string
      );
    },
    enabled: !!determinedSelectedMembership,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 1, // Only retry once, then fall back to legacy logic
  });

  // Get medicalProvider with priority: API > user document > surveySession
  const apiMedicalProvider = apiProductData?.mso;
  const fallbackMedicalProvider = getMedicalProvider(
    userProfile,
    surveySession
  );
  const effectiveMedicalProvider =
    apiMedicalProvider || fallbackMedicalProvider;

  // Update mso only if the value has changed
  useEffect(() => {
    if (effectiveMedicalProvider && effectiveMedicalProvider !== mso) {
      setMso(effectiveMedicalProvider);
    }
  }, [effectiveMedicalProvider, mso, setMso]);

  // Use effectiveMedicalProvider (synchronously computed from cached React Query data)
  // rather than mso (persisted store value) to avoid a loader flash:
  // clearPersistedStore() clears mso but setMso() is called in a useEffect, which
  // runs after render. effectiveMedicalProvider is computed on every render from
  // userProfile/surveySession cache and resolves immediately when that data is available.
  const isLoading =
    !effectiveMedicalProvider && (isLoadingApiProduct || isFetchingApiProduct);

  return {
    medicalProvider: effectiveMedicalProvider || "",
    // Also return API data for components that need app_entrypoint
    apiProductData,
    // Return loading state for components that need to show loading UI
    isLoading,
  };
};

/**
 * Hook to get effective medical provider with fallback logic.
 * Now uses API data as the primary source, with fallback logic.
 *
 * Priority order:
 * 1. API product-route data (if product selected)
 * 2. Persisted mso from store (fastest, already loaded)
 * 3. User profile medicalProvider (user document takes precedence)
 * 4. Survey session medicalProvider (fallback)
 *
 * @param surveySessionData - Optional survey session data to use as fallback source
 * @returns The effective medical provider or undefined
 */
export const useEffectiveMedicalProvider = (
  surveySessionData?: Partial<SurveySession> | null
): MSO | undefined => {
  const { medicalProvider, apiProductData } = useGetMsoData();
  const { data: userProfile } = useCurrentUser();

  // Priority: API MSO > persisted MSO > computed from user/survey data
  const apiMso = apiProductData?.mso;
  const persistedMso = medicalProvider || undefined;
  const computedMso = getMedicalProvider(userProfile, surveySessionData);

  return (
    (apiMso as MSO | undefined) ||
    (persistedMso as MSO | undefined) ||
    computedMso
  );
};
